'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/getting-started/my-corner.mdx
 * (published at user-docs.totalaccess.co.za/getting-started/my-corner#managed-tasks)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  CheckSquare,
  Plus,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react';
import CapacitorModalHeader from '@/components/CapacitorModalHeader';
import TotalConnectFooter, { type TotalConnectTab } from '@/components/TotalConnectFooter';
import WorkItemsViewWrapper, { type WorkItemAssigneeOption } from '@/components/WorkItemsViewWrapper';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import WorkItemModal from '@/components/WorkItemModal';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import { useAuth } from '@/hooks/useAuth';
import { useBadge } from '@/contexts/BadgeContext';
import { useTheme } from '@/contexts/ThemeContext';
import { isCapacitor } from '@/lib/platformUtils';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import AnnouncementsPanel from '@/components/announcements/AnnouncementsPanel';
import UserDocsLink from '@/components/shared/UserDocsLink';

type ReportingScopeEmployee = {
  user_id: number;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  display_name?: string | null;
  email?: string | null;
};

type ReportingScopeResponse = {
  success: boolean;
  employees?: ReportingScopeEmployee[];
  scope?: {
    user_ids?: number[];
  };
};

const mapReportingScopeAssignee = (employee: ReportingScopeEmployee): WorkItemAssigneeOption | null => {
  if (typeof employee.user_id !== 'number' || !Number.isFinite(employee.user_id)) {
    return null;
  }

  const firstName = (typeof employee.first_name === 'string' && employee.first_name) ? employee.first_name.trim() : '';
  const lastName = (typeof employee.last_name === 'string' && employee.last_name) ? employee.last_name.trim() : '';
  const email = (typeof employee.email === 'string' && employee.email && employee.email.trim().length > 0) ? employee.email.trim() : undefined;
  const fullName = (typeof employee.full_name === 'string' && employee.full_name) ? employee.full_name.trim() : undefined;
  const displayName = (typeof employee.display_name === 'string' && employee.display_name) ? employee.display_name.trim() : undefined;

  const name = fullName
    ? fullName
    : displayName
      ? displayName
      : [firstName, lastName].filter(Boolean).join(' ') || email || `User ${employee.user_id}`;

  return {
    id: employee.user_id,
    name,
    email,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
  };
};

interface ManagedTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasTotalConnect?: boolean;
  onTabChange?: (tab: TotalConnectTab) => void;
  initialMainTab?: ManagedTasksMainTab;
}

export type ManagedTasksMainTab = 'tasks' | 'updates';

type MainTab = ManagedTasksMainTab;

const MANAGED_TAB_BADGE_CLASS = 'bg-blue-500 text-white';

export default function ManagedTasksModal({
  isOpen,
  onClose,
  hasTotalConnect = true,
  onTabChange,
  initialMainTab,
}: ManagedTasksModalProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { currentTheme, clientBranding } = useTheme();
  const { badgeCounts, refreshBadgeCounts } = useBadge();
  const isDark = currentTheme.mode === 'dark';
  const isMobileApp = isCapacitor();
  const [isResponsiveMobileViewport, setIsResponsiveMobileViewport] = useState(false);
  const isMobile = isMobileApp || isResponsiveMobileViewport;
  const primaryColor = clientBranding?.primary_color || currentTheme.colors.primary;

  const [mainTab, setMainTab] = useState<MainTab>('tasks');
  const [tasksViewKey, setTasksViewKey] = useState(0);
  const [showAttentionFilter, setShowAttentionFilter] = useState(false);
  const [showWorkItemModal, setShowWorkItemModal] = useState(false);
  const [selectedManagedTask, setSelectedManagedTask] = useState<WorkItem | null>(null);

  const taskAttentionCount = badgeCounts.tasksDueTodayCount;
  const isTasksTabSelected = mainTab === 'tasks';
  const isUpdatesTabSelected = mainTab === 'updates';

  const handleAttentionCountClick = useCallback(() => {
    setShowAttentionFilter((current) => !current);
  }, []);

  // Line manager task scope (similar to DynamicOperationalView)
  interface ReportingScopeEmployee {
    user_id: number;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    full_name?: string | null;
    display_name?: string | null;
  }

  interface ReportingScopeResponse {
    success?: boolean;
    employees?: ReportingScopeEmployee[];
    scope?: {
      user_ids?: number[];
      is_line_manager?: boolean;
    };
  }

  const buildSelfTaskAssignee = (user: { id?: number | null; employee_id?: number | null; first_name?: string | null; last_name?: string | null; email?: string | null } | null | undefined): WorkItemAssigneeOption[] => {
    if (!user?.id || !Number.isFinite(user.id)) {
      return [];
    }

    const firstName = typeof user.first_name === 'string' ? user.first_name.trim() : '';
    const lastName = typeof user.last_name === 'string' ? user.last_name.trim() : '';
    const email = typeof user.email === 'string' && user.email.trim().length > 0 ? user.email.trim() : undefined;
    const name = [firstName, lastName].filter(Boolean).join(' ') || email || `User ${user.id}`;

    return [{
      id: user.id,
      name,
      email,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
    }];
  };

  const userId = user?.id;
  const employeeId = user?.employee_id;
  const userFirstName = user?.first_name;
  const userLastName = user?.last_name;
  const userEmail = user?.email;

  const baseAssignedUserScope = useMemo(() => {
    if (!userId || !Number.isFinite(userId)) {
      return [] as number[];
    }
    return [userId];
  }, [userId]);

  const baseAvailableAssignees = useMemo(
    () => buildSelfTaskAssignee({
      id: userId,
      employee_id: employeeId,
      first_name: userFirstName,
      last_name: userLastName,
      email: userEmail,
    }),
    [employeeId, userEmail, userFirstName, userId, userLastName]
  );

  const [assignedUserScope, setAssignedUserScope] = useState<number[]>(baseAssignedUserScope);
  const [availableAssignees, setAvailableAssignees] = useState<WorkItemAssigneeOption[]>(baseAvailableAssignees);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setAssignedUserScope(baseAssignedUserScope);
    setAvailableAssignees(baseAvailableAssignees);

    if (!employeeId || !Number.isFinite(employeeId)) {
      return;
    }

    let isCancelled = false;

    const fetchReportingScope = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          return;
        }

        const params = new URLSearchParams({
          employee_id: String(employeeId),
          include_descendants: 'true',
          include_self: 'true',
        });

        const response = await fetch(`/api/v1/employees/direct-reports?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const payload = await response.json() as ReportingScopeResponse;
        if (!response.ok || !payload?.success) {
          throw new Error('Failed to load reporting scope');
        }

        const assigneeMap = new Map<number, WorkItemAssigneeOption>();
        baseAvailableAssignees.forEach((assignee) => {
          assigneeMap.set(assignee.id, assignee);
        });

        (payload.employees || []).forEach((employee) => {
          const assignee = mapReportingScopeAssignee(employee);
          if (assignee) {
            assigneeMap.set(assignee.id, assignee);
          }
        });

        const nextScope = Array.from(new Set([
          ...baseAssignedUserScope,
          ...((payload.scope?.user_ids || []).filter((value): value is number => typeof value === 'number' && Number.isFinite(value))),
          ...(payload.employees || [])
            .map((employee) => employee.user_id)
            .filter((value): value is number => typeof value === 'number' && Number.isFinite(value)),
        ]));

        if (!isCancelled) {
          setAssignedUserScope(nextScope.length > 0 ? nextScope : baseAssignedUserScope);
          setAvailableAssignees(Array.from(assigneeMap.values()));
        }
      } catch (error) {
        console.error('[ManagedTasksModal] Failed to load reporting scope:', error);

        if (!isCancelled) {
          setAssignedUserScope(baseAssignedUserScope);
          setAvailableAssignees(baseAvailableAssignees);
        }
      }
    };

    void fetchReportingScope();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, baseAssignedUserScope, baseAvailableAssignees, employeeId, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateResponsiveMobileViewport = () => {
      setIsResponsiveMobileViewport(window.matchMedia('(max-width: 1023.98px)').matches);
    };

    updateResponsiveMobileViewport();
    window.addEventListener('resize', updateResponsiveMobileViewport);
    window.addEventListener('orientationchange', updateResponsiveMobileViewport);

    return () => {
      window.removeEventListener('resize', updateResponsiveMobileViewport);
      window.removeEventListener('orientationchange', updateResponsiveMobileViewport);
    };
  }, []);


  const handleFooterTabChange = useCallback((tab: TotalConnectTab) => {
    if (tab === 'tasks') return;

    if (onTabChange) {
      onTabChange(tab);
      return;
    }

    onClose();
    if (tab === 'dashboard') {
      router.push('/client-zone/dashboard');
    }
  }, [onClose, onTabChange, router]);

  useEffect(() => {
    if (!isOpen) {
      setMainTab('tasks');
      return;
    }

    void refreshBadgeCounts();
  }, [isOpen, refreshBadgeCounts]);

  useEffect(() => {
    if (!isOpen || !initialMainTab) {
      return;
    }

    setMainTab(initialMainTab);
  }, [initialMainTab, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined' || mainTab !== 'updates') {
      return;
    }

    const interval = window.setInterval(() => {
      window.dispatchEvent(new CustomEvent('ta:announcements-refresh'));
    }, 45000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isOpen, mainTab]);

  if (!isOpen) return null;

  const showTopSafeAreaSpacer = isMobile;
  const mobileBackdropZIndexClass = isMobile ? 'z-[84]' : 'z-[89]';
  const mobilePanelZIndexClass = isMobile ? 'z-[85]' : 'z-[90]';

  const mobileTabToggleRow = (
    <div className="m-3 flex flex-wrap items-center gap-2 rounded-xl bg-gray-100 p-2 dark:bg-gray-800">
      <button
        type="button"
        onClick={() => setMainTab('tasks')}
        className={`relative inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${isTasksTabSelected
          ? 'border-blue-200 bg-blue-50 text-[#047cde] shadow-sm dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200'
          : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-100'
          }`}
      >
        <CheckSquare className="h-3.5 w-3.5 shrink-0" />
        <span>Tasks</span>
        {taskAttentionCount > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full text-[9px] font-bold ${MANAGED_TAB_BADGE_CLASS}`}>
            {taskAttentionCount > 99 ? '99+' : taskAttentionCount}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() => setMainTab('updates')}
        className={`relative inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${isUpdatesTabSelected
          ? 'border-blue-200 bg-blue-50 text-[#047cde] shadow-sm dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200'
          : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-100'
          }`}
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        <span>Updates</span>
      </button>
    </div>
  );

  return (
    <React.Fragment>
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300 ${mobileBackdropZIndexClass}`}
        onClick={onClose}
      />

      <div
        className={`fixed ${mobilePanelZIndexClass} animate-slide-in-right right-0 w-full shadow-2xl flex flex-col ${isDark ? 'bg-gray-900' : 'bg-white'
          } ${isMobile ? 'max-w-full' : 'max-w-6xl'}`}
        style={{
          ...(isMobile ? {
            top: '0px',
            bottom: 'var(--safe-area-inset-bottom)',
            transition: 'top 300ms ease',
          } : {
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            top: 'var(--safe-area-inset-top)',
            bottom: 'var(--safe-area-inset-bottom)',
            height: 'calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))',
          })
        }}
      >
        {showTopSafeAreaSpacer && (
          <div
            aria-hidden="true"
            className={`flex-shrink-0 ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            style={{ height: MOBILE_SAFE_AREA_TOP }}
          />
        )}

        <CapacitorModalHeader
          icon={isTasksTabSelected ? CheckSquare : Sparkles}
          title={isTasksTabSelected ? 'Managed Tasks' : 'Updates & release notes'}
          accentColor={primaryColor}
          isDark={isDark}
          className={!isMobile ? '[&>div]:px-0' : ''}
          actions={
            <>
              <UserDocsLink
                docsPath="/getting-started/my-corner#managed-tasks"
                label="User guide: Managed Tasks"
              />
              {!isMobile && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMainTab('tasks')}
                    className={`relative inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${isTasksTabSelected
                      ? 'border-blue-200 bg-blue-50 text-[#047cde] shadow-sm dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200'
                      : isDark
                        ? 'border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <CheckSquare className="h-3.5 w-3.5 shrink-0" />
                    <span>Tasks</span>
                    {taskAttentionCount > 0 && (
                      <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full text-[9px] font-bold ${MANAGED_TAB_BADGE_CLASS}`}>
                        {taskAttentionCount > 99 ? '99+' : taskAttentionCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMainTab('updates')}
                    className={`relative inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors ${isUpdatesTabSelected
                      ? 'border-blue-200 bg-blue-50 text-[#047cde] shadow-sm dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200'
                      : isDark
                        ? 'border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <Sparkles className="h-3.5 w-3.5 shrink-0" />
                    <span>Updates</span>
                  </button>
                </div>
              )}

              {mainTab === 'tasks' ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowWorkItemModal(true)}
                    className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors"
                    style={{ backgroundColor: primaryColor, color: '#ffffff' }}
                    title="Create new task"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">New task</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTasksViewKey((current) => current + 1)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Refresh tasks"
                  >
                    <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('ta:announcements-refresh'))}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Refresh updates"
                >
                  <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Close"
              >
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </>
          }
        />

        <div className="flex-1 min-h-0 overflow-hidden">
          {mainTab === 'tasks' ? (
            <div className="flex h-full min-h-0 flex-col">
              {isMobile && mobileTabToggleRow}
              <div className="min-h-0 flex-1 overflow-hidden">
                <WorkItemsViewWrapper
                  key={`managed-tasks-${tasksViewKey}`}
                  activeTab="managed-tasks-modal"
                  workItemType="task,subtask"
                  filterByAssignedUser
                  assignedOnly
                  personalMode
                  assignedUserScope={assignedUserScope}
                  availableAssignees={availableAssignees}
                  hideAssigneeSelectAll
                  attentionCount={taskAttentionCount}
                  onAttentionCountClick={handleAttentionCountClick}
                  onItemClick={(item: WorkItem) => setSelectedManagedTask(item)}
                  activeHeaderStatFilter={showAttentionFilter ? 'overdue' : null}
                  onActiveHeaderStatFilterChange={(filter) => setShowAttentionFilter(filter === 'overdue')}
                  showHeader={false}
                  showFilterBar
                  filterBarCompact
                  filterBarSingleRow
                  showFilterBarStats={false}
                  showFilterBarPresets={false}
                  showFilterBarQuickFilters={false}
                  showFilterBarTypeFilter={false}
                  defaultViewMode="list"
                />
              </div>
            </div>
          ) : (
            <>
              {/* Mobile tab toggle */}
              {isMobile && mobileTabToggleRow}
              <div className="h-full overflow-y-auto p-4">
                <div className="space-y-3">
                  <div className={`flex flex-col gap-2 rounded-xl border px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
                    <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="font-semibold">Platform updates</span>
                      {' · '}Release notes for new features and important system notices.
                    </p>
                  </div>
                  <AnnouncementsPanel />
                </div>
              </div>
            </>
          )}

          <TotalConnectFooter
            activeTab="tasks"
            onTabChange={handleFooterTabChange}
            hasTotalConnect={hasTotalConnect}
            tasksBadge={taskAttentionCount}
          />
        </div>
      </div>

      {showWorkItemModal && (
        <WorkItemModal
          isOpen={showWorkItemModal}
          onClose={() => setShowWorkItemModal(false)}
          assigneeUserIds={assignedUserScope}
          onSave={async (itemData) => {
            try {
              const token = authService.getTokens()?.access_token;
              if (!token) throw new Error('No authentication token');

              const response = await fetch('/api/v1/work-items', {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  ...itemData,
                  item_type: 'task',
                  module_context: 'total-operations',
                }),
              });

              const data = await response.json();
              if (data.success) {
                setShowWorkItemModal(false);
                setTasksViewKey((current) => current + 1);
                void refreshBadgeCounts();
                notify.success('Task created successfully');
              } else {
                notify.error(data.message || 'Unable to create task. Please try again.');
              }
            } catch (error) {
              console.error('[ManagedTasksModal] Error creating task:', error);
              notify.error('Unable to create task. Please try again.');
            }
          }}
          itemType="task"
          templates={[]}
          headerColor="from-blue-500 to-blue-600"
          icon={CheckSquare}
          label="New Task"
          description="Create a new managed task"
          slideOverLayout
        />
      )}

      {selectedManagedTask && (
        <WorkItemDetailModal
          isOpen={Boolean(selectedManagedTask)}
          onClose={() => setSelectedManagedTask(null)}
          workItem={selectedManagedTask}
          onRefresh={() => setTasksViewKey((prev) => prev + 1)}
          workspaceItemMode
          visibleTabs={['overview', 'work_breakdown', 'comments', 'attachments', 'history']}
        />
      )}
    </React.Fragment>
  );
}
